<template>
  <div>
    <v-toolbar flat>
      <v-toolbar-title class="text-h5">
        {{ $t('entities.invoiceSettings.title') }}
      </v-toolbar-title>
      <v-spacer />
      <RefreshButton :refresh="fetchInvoiceSettings" :loading="isLoading" />
    </v-toolbar>
    <v-divider />
    <v-card-text>
      <v-skeleton-loader
        v-if="isLoading"
        type="list-item-two-line, list-item-two-line" />
      <v-alert v-else-if="!invoiceSettings" type="info" class="mb-0">
        {{ $t('components.noData.noEntriesFound') }}
      </v-alert>
      <v-form v-else @submit.prevent="saveInvoiceSettings">
        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.default_payment_method_code"
              :items="paymentMethodOptions"
              :label="
                $t('entities.invoiceSettings.attributes.defaultPaymentMethod')
              "
              :disabled="!canEditInvoiceSettings"
              outlined
              hide-details />
          </v-col>
          <v-col cols="12" md="6" class="d-flex align-center">
            <v-switch
              v-model="form.automatically_open_print_upon_invoice_creation"
              :label="
                $t('entities.invoiceSettings.attributes.automaticallyOpenPrint')
              "
              :disabled="!canEditInvoiceSettings"
              class="mt-0"
              hide-details />
          </v-col>
        </v-row>
        <div v-if="canEditInvoiceSettings" class="d-flex justify-end mt-6">
          <v-btn
            type="submit"
            color="primary"
            :disabled="!isDirty"
            :loading="isSaving">
            {{ $t('miscellaneous.save') }}
          </v-btn>
        </div>
      </v-form>
    </v-card-text>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import InvoiceSettings from '@/models/shop/InvoiceSettings'
import { PaymentMethodCode } from '@/models/shop/PaymentMethod.types'
import RefreshButton from '@/components/crud/RefreshButton.vue'
import isRole from '@/mixins/crud/computed/isRole'

export default {
  name: 'InvoiceSettingsTab',
  components: {
    RefreshButton,
  },
  mixins: [isRole],
  data() {
    return {
      isLoading: false,
      isSaving: false,
      invoiceSettings: null,
      form: {
        automatically_open_print_upon_invoice_creation: true,
        default_payment_method_code: PaymentMethodCode.CSH,
      },
    }
  },
  computed: {
    arenaId() {
      return this.$store.getters.arena ? this.$store.getters.arena.id : null
    },
    canEditInvoiceSettings() {
      return this.isAdmin || this.isManager
    },
    paymentMethodOptions() {
      return Object.values(PaymentMethodCode).map((code) => ({
        text: this.$t(`miscellaneous.paymentMethodsShortNames.${code}`),
        value: code,
      }))
    },
    isDirty() {
      if (!this.invoiceSettings) return false
      return (
        this.form.automatically_open_print_upon_invoice_creation !==
          this.invoiceSettings.automatically_open_print_upon_invoice_creation ||
        this.form.default_payment_method_code !==
          this.invoiceSettings.default_payment_method_code
      )
    },
  },
  methods: {
    ...mapActions(['showSnackbar']),
    async fetchInvoiceSettings() {
      if (!this.arenaId) {
        this.invoiceSettings = null
        return
      }
      this.isLoading = true
      try {
        const response = await InvoiceSettings.api.getArenaInvoiceSettings(
          this.arenaId,
        )
        this.invoiceSettings = new InvoiceSettings(response?.data || {})
        this.resetForm()
      } catch (e) {
        console.error(e)
        this.invoiceSettings = null
      } finally {
        this.isLoading = false
      }
    },
    resetForm() {
      if (!this.invoiceSettings) return
      this.form = {
        automatically_open_print_upon_invoice_creation:
          this.invoiceSettings.automatically_open_print_upon_invoice_creation,
        default_payment_method_code:
          this.invoiceSettings.default_payment_method_code,
      }
    },
    async saveInvoiceSettings() {
      if (!this.arenaId || !this.isDirty) return
      this.isSaving = true
      try {
        const response = await InvoiceSettings.api.updateArenaInvoiceSettings(
          this.arenaId,
          {
            automatically_open_print_upon_invoice_creation:
              this.form.automatically_open_print_upon_invoice_creation,
            default_payment_method_code: this.form.default_payment_method_code,
          },
        )
        this.invoiceSettings = new InvoiceSettings(response?.data || {})
        this.resetForm()
        this.showSnackbar({
          color: 'success',
          message: this.$t('entities.invoiceSettings.updateSuccess'),
        })
      } catch (e) {
        console.error(e)
        this.showSnackbar({
          color: 'error',
          message: this.$t('entities.invoiceSettings.updateFailed'),
        })
      } finally {
        this.isSaving = false
      }
    },
  },
  watch: {
    arenaId: {
      immediate: true,
      handler() {
        return this.fetchInvoiceSettings()
      },
    },
  },
}
</script>
